import { Component } from "@theme/component";
import {
  demoDefaults,
  demoAllowed,
  demoRequested,
  readDemoConfig,
  saveDemoConfig,
} from "@theme/raptor-demo";

/** Preview review controls. Never edits a Giveaway Ninja rule or awards an entry.
 * @extends {Component<{boost: HTMLSelectElement, rate: HTMLElement}>}
 */
class RaptorPromotionPreview extends Component {
  static observedAttributes = ["hidden"];

  attributeChangedCallback(name, before, after) {
    if (
      name === "hidden" &&
      before !== after &&
      after !== null &&
      this.isConnected
    ) {
      queueMicrotask(() => {
        if (this.isConnected) this.updatedCallback();
      });
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.abort?.abort();
    this.bind();
  }

  bind() {
    if (!demoAllowed() || !demoRequested()) {
      this.hidden = true;
      this.abort?.abort();
      return;
    }
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    try {
      const saved = readDemoConfig();
      saveDemoConfig({
        ...(saved || demoDefaults),
        subscriptionBonus: Math.max(0, Number(this.dataset.bonus) || 0),
      });
    } catch {
      // A browser storage restriction leaves the native calculator unchanged.
    }
    this.hidden = false;
    this.refs.boost.addEventListener(
      "change",
      () => {
        const multiplier = Number(this.refs.boost.value);
        try {
          saveDemoConfig({
            ...(readDemoConfig() || demoDefaults),
            scenario: multiplier === 1 ? "base" : "promo",
            promoMultiplier: multiplier === 1 ? 2 : multiplier,
            subscriptionBonus: Math.max(0, Number(this.dataset.bonus) || 0),
          });
        } catch {
          this.refs.boost.disabled = true;
        }
      },
      { signal },
    );
    document.addEventListener("raptor:config-change", () => this.update(), {
      signal,
    });
    window.addEventListener("storage", () => this.update(), { signal });
    this.update();
  }

  update() {
    const config = readDemoConfig();
    const multiplier =
      config?.scenario === "promo" ? config.promoMultiplier || 2 : 1;
    this.refs.boost.value = String(multiplier);
    this.refs.rate.textContent = this.dataset.rateLabel
      .replace("{rate}", String((config?.entriesPerDollar || 5) * multiplier))
      .replace(
        "{bonus}",
        new Intl.NumberFormat().format(config?.subscriptionBonus || 0),
      );
  }

  disconnectedCallback() {
    this.abort?.abort();
    super.disconnectedCallback();
  }
}
if (!customElements.get("raptor-promotion-preview"))
  customElements.define("raptor-promotion-preview", RaptorPromotionPreview);
